/**
 * Data-fetching hooks for the Common Dashboard Framework.
 *
 * - useDashboardData: generic loader (loading/error/data + refetch + optional
 *   auto-refresh) with request cancellation on param change/unmount.
 * - useKpis: convenience wrapper over POST /api/dashboard/v2/kpis.
 *
 * No react-query dependency (the app doesn't use one); this provides the
 * subset dashboards need — dedup via AbortController, parallel-friendly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchKpis } from '../api/client';

function isCanceled(err) {
  return err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
}

/**
 * "Last updated" signal.
 *
 * A dashboard header wants to tell the reader how fresh what they are looking
 * at is. Widgets fetch independently, so no single call owns that timestamp —
 * the honest answer is "when the most recent widget load succeeded". Every
 * successful load through this hook announces itself here, and a shell can
 * subscribe. Deliberately NOT tied to when the user pressed Refresh: that
 * would claim freshness before the data had arrived.
 */
const loadListeners = new Set();

export function onDashboardLoad(listener) {
  loadListeners.add(listener);
  return () => loadListeners.delete(listener);
}

function announceLoad() {
  const at = new Date();
  loadListeners.forEach((fn) => {
    try {
      fn(at);
    } catch {
      /* a broken listener must not break data loading */
    }
  });
}

/**
 * Generic async loader.
 * @param loader  (signal) => Promise<data>
 * @param deps    dependency array that should trigger a reload
 * @param options { enabled, refreshMs }
 */
export function useDashboardData(loader, deps = [], { enabled = true, refreshMs = 0 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const result = await loader(ctrl.signal);
      if (abortRef.current === ctrl) {
        setData(result);
        announceLoad();
      }
    } catch (err) {
      if (!isCanceled(err) && abortRef.current === ctrl) setError(err);
    } finally {
      if (abortRef.current === ctrl) setLoading(false);
    }
  }, [enabled, ...deps]); // deps supplied by caller (spread is intentional)

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!refreshMs || !enabled) return undefined;
    const id = setInterval(load, refreshMs);
    return () => clearInterval(id);
  }, [refreshMs, enabled, load]);

  return { data, loading, error, refetch: load };
}

/**
 * KPI loader for a module.
 * @param params { module, metrics:[keys], siteId, time, departmentId,
 *                 fyStartMonth, enabled, refreshMs }
 */
export function useKpis({
  module,
  metrics,
  siteId,
  time,
  departmentId,
  dimensions,
  fyStartMonth,
  compare = false,
  standard = 'osha',
  enabled = true,
  refreshMs = 0,
}) {
  const metricsKey = Array.isArray(metrics) ? metrics.join(',') : '';
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';
  const ready = enabled && !!module && Array.isArray(metrics) && metrics.length > 0;

  const loader = useCallback(
    (signal) => {
      const body = { module, metrics, time };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      if (compare) body.compare = true;
      if (standard) body.standard = standard;
      return fetchKpis(body, { signal });
    },
    // metrics/time/dimensions referenced via their stable string keys
    [module, metricsKey, siteId, timeKey, dimsKey, departmentId, fyStartMonth, compare, standard],
  );

  const { data, loading, error, refetch } = useDashboardData(
    loader,
    [module, metricsKey, siteId, timeKey, dimsKey, departmentId, fyStartMonth, compare, standard],
    { enabled: ready, refreshMs },
  );

  return {
    data,
    metrics: data?.metrics || {},
    range: data?.range || null,
    unauthorized: data?.unauthorized || [],
    invalid: data?.invalid || [],
    loading,
    error,
    refetch,
  };
}

export default useDashboardData;
